import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, FREE_LIMIT } from '../context/AuthContext';
import { analytics } from '../lib/analytics';

const MONTHLY_PRICE_ID = process.env.REACT_APP_STRIPE_PRICE_MONTHLY;
const ANNUAL_PRICE_ID  = process.env.REACT_APP_STRIPE_PRICE_ANNUAL;

export default function PremiumPage() {
  const { user, profile, isPro, isAdmin, entryCount, remainingFree, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [billing, setBilling]   = useState('annual'); // 'monthly' | 'annual'
  const [loading, setLoading]   = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [notice, setNotice]     = useState('');

  // Handle return from Stripe
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setNotice('success');
      refreshProfile();
    } else if (searchParams.get('canceled') === 'true') {
      setNotice('canceled');
    }
  }, [searchParams, refreshProfile]);

  const handleUpgrade = async () => {
    if (!user) { navigate('/auth'); return; }
    setLoading(true);
    try {
      const priceId = billing === 'annual' ? ANNUAL_PRICE_ID : MONTHLY_PRICE_ID;
      analytics.checkoutStarted({ plan: 'pro', billing });
      const res = await fetch('/api/billing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId,
          userId: user.id,
          userEmail: user.email,
          billingCycle: billing,
        }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else throw new Error(data.error || 'Failed to create checkout session');
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setLoading(false);
  };

  const handleManageBilling = async () => {
    if (!profile?.stripe_customer_id) return;
    setPortalLoading(true);
    try {
      const res = await fetch('/api/billing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: profile.stripe_customer_id }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else throw new Error(data.error);
    } catch (e) { alert('Error: ' + e.message); }
    setPortalLoading(false);
  };

  const monthlyPrice = 9;
  const annualPrice  = 79;
  const annualMonthly = (annualPrice / 12).toFixed(2);
  const savings = Math.round(((monthlyPrice * 12 - annualPrice) / (monthlyPrice * 12)) * 100);

  return (
    <div style={s.page}>
      <div style={s.inner}>

        {/* Header */}
        <div style={s.topRow}>
          <div>
            <div style={s.pageTitle}>
              {isPro || isAdmin ? 'Your Pro plan' : 'Upgrade to Pro'}
            </div>
            <div style={s.pageSub}>
              {isPro || isAdmin
                ? 'Manage your subscription and billing'
                : 'Run Skills & Playbooks, unlimited installs, publish to Marketplace, and more'}
            </div>
          </div>
          <button onClick={() => navigate('/app')} style={s.backBtn}>← Back</button>
        </div>

        {/* Success / canceled notice */}
        {notice === 'success' && (
          <div style={{ ...s.notice, background: 'rgba(29,158,117,0.1)', borderColor: 'rgba(29,158,117,0.3)', color: '#5dcaa5' }}>
            🎉 Welcome to Pro! Your account has been upgraded. Enjoy unlimited everything.
          </div>
        )}
        {notice === 'canceled' && (
          <div style={{ ...s.notice, background: 'var(--surface2)', borderColor: 'var(--border2)', color: 'var(--muted)' }}>
            No worries — you can upgrade anytime.
          </div>
        )}

        {/* Current plan status card */}
        <div style={s.statusCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={s.statusLabel}>Current plan</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                <span style={{
                  fontSize: 13, fontWeight: 500, padding: '3px 12px', borderRadius: 20,
                  background: (isPro || isAdmin) ? 'var(--accent-dim)' : 'var(--surface2)',
                  border: `0.5px solid ${(isPro || isAdmin) ? 'var(--accent-border)' : 'var(--border2)'}`,
                  color: (isPro || isAdmin) ? 'var(--accent)' : 'var(--muted)',
                }}>
                  {isAdmin ? '🛡 Admin' : isPro ? '⚡ Pro' : '○ Free'}
                </span>
                {isPro && profile?.plan_expires_at && (
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                    Renews {new Date(profile.plan_expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
              </div>
            </div>

            {/* Usage bar — free only */}
            {!isPro && !isAdmin && (
              <div style={{ minWidth: 180 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span style={{ fontSize: 11, color: 'var(--dim)' }}>Saves used</span>
                  <span style={{ fontSize: 11, color: entryCount >= FREE_LIMIT ? 'var(--danger)' : 'var(--dim)' }}>
                    {entryCount} / {FREE_LIMIT}
                  </span>
                </div>
                <div style={{ height: 5, background: 'var(--surface2)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 3,
                    width: `${Math.min((entryCount / FREE_LIMIT) * 100, 100)}%`,
                    background: entryCount >= FREE_LIMIT ? 'var(--danger)' : 'var(--accent)',
                    transition: 'width 0.3s',
                  }} />
                </div>
                <div style={{ fontSize: 10, color: 'var(--dim)', marginTop: 3, textAlign: 'right' }}>
                  {remainingFree} save{remainingFree !== 1 ? 's' : ''} remaining
                </div>
              </div>
            )}

            {/* Manage billing — Pro users */}
            {isPro && profile?.stripe_customer_id && (
              <button onClick={handleManageBilling} disabled={portalLoading} style={s.manageBtn}>
                {portalLoading ? 'Loading...' : 'Manage billing →'}
              </button>
            )}
          </div>
        </div>

        {/* Billing toggle — only show if not already Pro */}
        {!isPro && !isAdmin && (
          <>
            <div style={s.toggleWrap}>
              <button
                style={{ ...s.toggleBtn, ...(billing === 'monthly' ? s.toggleBtnOn : {}) }}
                onClick={() => setBilling('monthly')}>
                Monthly
              </button>
              <button
                style={{ ...s.toggleBtn, ...(billing === 'annual' ? s.toggleBtnOn : {}) }}
                onClick={() => setBilling('annual')}>
                Annual
                <span style={{
                  marginLeft: 7, fontSize: 10, padding: '2px 7px',
                  background: 'rgba(29,158,117,0.15)', color: '#5dcaa5',
                  border: '0.5px solid rgba(29,158,117,0.3)', borderRadius: 10,
                }}>Save {savings}%</span>
              </button>
            </div>

            {/* Plan card */}
            <div style={s.planCard}>
              <div style={s.planBadge}>Most popular</div>

              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 4 }}>
                <span style={s.planPrice}>
                  ${billing === 'annual' ? annualMonthly : monthlyPrice}
                </span>
                <span style={s.planPer}>/mo</span>
              </div>

              {billing === 'annual' && (
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
                  Billed as <strong style={{ color: 'var(--text)' }}>${annualPrice}/year</strong>
                  <span style={{ marginLeft: 8, color: '#5dcaa5' }}>
                    (save ${monthlyPrice * 12 - annualPrice} vs monthly)
                  </span>
                </div>
              )}
              {billing === 'monthly' && (
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
                  Billed monthly · cancel anytime
                </div>
              )}

              <div style={{ ...s.divider, margin: '16px 0' }} />

              <ul style={s.featureList}>
                {[
                  ['Unlimited saved prompts',                              null],
                  ['Run Skills & Playbooks',                               'pro'],
                  ['Unlimited skill & playbook installs',                  null],
                  ['Publish skills & playbooks to Marketplace',            null],
                  ['Public & private skills',                              null],
                  ['Unlimited folders & skill creation',                   null],
                  ['20 AI generations/mo — Smart Compress & SOP Builder', 'ai'],
                  ['VS Code extension + CLI',                              null],
                  ['JSON & CSV export',                                    null],
                  ['Priority support',                                     null],
                ].map(([feat, badge]) => (
                  <li key={feat} style={{ ...s.featureItem, alignItems: 'flex-start' }}>
                    <span style={{ color: 'var(--accent)', fontSize: 12, width: 16, flexShrink: 0, marginTop: 2 }}>✓</span>
                    <span style={{ flex: 1, lineHeight: 1.4 }}>
                      {feat}
                      {badge === 'pro' && (
                        <span style={{ marginLeft: 7, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: 'var(--accent-dim)', color: 'var(--accent)', border: '0.5px solid var(--accent-border)', verticalAlign: 'middle' }}>PRO ONLY</span>
                      )}
                      {badge === 'ai' && (
                        <span style={{ marginLeft: 7, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: 'rgba(29,158,117,0.12)', color: '#1d9e75', border: '0.5px solid rgba(29,158,117,0.3)', verticalAlign: 'middle' }}>AI</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={handleUpgrade}
                disabled={loading}
                style={s.upgradeBtn}>
                {loading
                  ? 'Redirecting to Stripe...'
                  : `Upgrade — $${billing === 'annual' ? annualPrice + '/yr' : monthlyPrice + '/mo'}`}
              </button>

              <div style={{ fontSize: 11, color: 'var(--dim)', textAlign: 'center', marginTop: 10 }}>
                Secure payment via Stripe · Cancel anytime
              </div>
            </div>

            {/* Compare */}
            <div style={s.compareWrap}>
              <div style={s.compareLabel}>What's included on the free plan</div>
              <ul style={{ ...s.featureList, marginTop: 8 }}>
                {[
                  ['10 saved prompts',                  true,  null],
                  ['Browse Skills & Playbooks',         true,  null],
                  ['3 skill installs',                  true,  'limit'],
                  ['1 playbook install',                true,  'limit'],
                  ['Create up to 5 skills',             true,  'limit'],
                  ['Create up to 2 playbooks',         true,  'limit'],
                  ['2 folders',                         true,  'limit'],
                  ['Chrome extension',                  true,  null],
                  ['Run Skills & Playbooks',            false, null],
                  ['Publish to Marketplace',            false, null],
                  ['AI generations',                    false, null],
                  ['Unlimited installs',                false, null],
                  ['Private skills',                    false, null],
                  ['Export (JSON/CSV)',                 false, null],
                ].map(([feat, yes, badge]) => (
                  <li key={feat} style={s.featureItem}>
                    <span style={{ color: yes ? '#1d9e75' : 'var(--dim)', fontSize: 12, width: 16, flexShrink: 0 }}>
                      {yes ? '✓' : '✗'}
                    </span>
                    <span style={{ color: yes ? 'var(--muted)' : 'var(--dim)', flex: 1 }}>{feat}</span>
                    {badge === 'limit' && (
                      <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 8, background: 'rgba(239,159,39,0.1)', color: '#ef9f27', border: '0.5px solid rgba(239,159,39,0.25)' }}>
                        limited
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        {/* Already Pro — show features summary */}
        {(isPro || isAdmin) && !notice && (
          <div style={s.proActive}>
            <div style={{ fontSize: 22, marginBottom: 12 }}>⚡</div>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
              You have full Pro access
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7 }}>
              Unlimited saves · Run & publish Skills/Playbooks · Unlimited installs · Private skills · Unlimited folders · 20 AI generations/mo · VS Code + CLI
            </div>
            {isPro && profile?.stripe_customer_id && (
              <button onClick={handleManageBilling} disabled={portalLoading} style={{ ...s.manageBtn, marginTop: 18 }}>
                {portalLoading ? 'Loading...' : 'Manage subscription & billing →'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'var(--bg)', fontFamily: 'var(--font)', padding: '24px 16px' },
  inner: { maxWidth: 520, margin: '0 auto' },
  topRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 12 },
  pageTitle: { fontSize: 22, fontWeight: 500, color: 'var(--text)', marginBottom: 4, letterSpacing: '-0.02em' },
  pageSub: { fontSize: 13, color: 'var(--muted)' },
  backBtn: {
    background: 'none', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)',
    padding: '7px 14px', color: 'var(--muted)', fontFamily: 'var(--font)', fontSize: 13, cursor: 'pointer', flexShrink: 0,
  },
  notice: {
    border: '0.5px solid', borderRadius: 'var(--radius)', padding: '12px 16px',
    fontSize: 13, lineHeight: 1.6, marginBottom: 16,
  },
  statusCard: {
    background: 'var(--surface)', border: '0.5px solid var(--border)',
    borderRadius: 'var(--radius-lg)', padding: '16px 20px', marginBottom: 20,
  },
  statusLabel: { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--dim)', marginBottom: 2 },
  manageBtn: {
    background: 'none', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)',
    padding: '7px 14px', fontFamily: 'var(--font)', fontSize: 12.5, color: 'var(--muted)', cursor: 'pointer',
  },
  toggleWrap: {
    display: 'flex', background: 'var(--surface)', border: '0.5px solid var(--border)',
    borderRadius: 'var(--radius)', padding: 4, gap: 4, marginBottom: 16,
  },
  toggleBtn: {
    flex: 1, padding: '9px 12px', border: 'none', borderRadius: 8,
    background: 'transparent', color: 'var(--muted)', fontFamily: 'var(--font)',
    fontSize: 13, cursor: 'pointer', transition: 'all 0.15s',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
  },
  toggleBtnOn: {
    background: 'var(--surface2)', color: 'var(--text)', fontWeight: 500,
    boxShadow: '0 0 0 0.5px var(--border2)',
  },
  planCard: {
    background: 'linear-gradient(160deg, rgba(127,119,221,0.06) 0%, var(--surface) 60%)',
    border: '1.5px solid var(--accent-border)',
    borderRadius: 'var(--radius-lg)', padding: '28px 24px', position: 'relative',
    marginBottom: 14,
  },
  planBadge: {
    position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)',
    background: 'var(--accent)', color: '#fff', fontSize: 10, fontWeight: 500,
    padding: '3px 14px', borderRadius: 20, whiteSpace: 'nowrap',
  },
  planPrice: { fontSize: 42, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.04em', lineHeight: 1 },
  planPer: { fontSize: 14, color: 'var(--muted)', paddingBottom: 6 },
  divider: { height: '0.5px', background: 'var(--border)' },
  featureList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 22 },
  featureItem: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--text)' },
  upgradeBtn: {
    width: '100%', background: 'var(--accent)', color: '#fff', border: 'none',
    borderRadius: 'var(--radius-sm)', padding: '13px', fontFamily: 'var(--font)',
    fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'opacity 0.15s',
  },
  compareWrap: {
    background: 'var(--surface)', border: '0.5px solid var(--border)',
    borderRadius: 'var(--radius-lg)', padding: '16px 20px',
  },
  compareLabel: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--dim)' },
  proActive: {
    background: 'var(--surface)', border: '0.5px solid var(--border)',
    borderRadius: 'var(--radius-lg)', padding: '32px 24px', textAlign: 'center',
  },
};
